import React, {
  useState,
  useCallback
} from 'react';
import PropTypes from 'prop-types';
import { TableRow } from '@mui/material';
import TableCell from 'src/components/Table/TableCell';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import MoreMenu from 'src/components/MoreMenu/MoreMenu';
import AddBoxIcon from '@mui/icons-material/AddBox';
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';
import { Link } from 'react-router-dom';
import ConfirmationDialog from 'src/components/ConfirmDialog/ConfirmationDialog';
import {
  unassignClabe,
  assignClabe,
  UNASSIGN_SANTANDER_CLABE
} from 'src/store/actions/santanderClabeActions';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import AssignClientDialog from './AssignClientDialog';
import SANTANDER_CLABE_ACTIONS from './MenuOptions';

const getMenuOptions = isAssigned => {
  const options = [];

  if (isAssigned) {
    options.push({
      name: SANTANDER_CLABE_ACTIONS.UNASSIGN,
      label: 'ADMINISTRATOR_VIEW.SANTANDER_CLABES.UNASSIGN_CLABE',
      icon: <DisabledByDefaultIcon />
    });
  } else {
    options.push({
      name: SANTANDER_CLABE_ACTIONS.ASSIGN,
      label: 'ADMINISTRATOR_VIEW.SANTANDER_CLABES.ASSIGN_CLABE',
      icon: <AddBoxIcon />
    });
  }

  return options;
};

const SantanderClabesRow = ({ clabe, client }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [openAssign, setOpenAssign] = useState(false);
  const [openUnassign, setOpenUnassign] = useState(false);

  const isAssigned = Boolean(client);
  const isUnassigning = useSelector(
    isLoadingSelector([UNASSIGN_SANTANDER_CLABE.action])
  );

  const menuOptions = getMenuOptions(isAssigned);

  const handleMenuClick = optionName => {
    if (optionName === SANTANDER_CLABE_ACTIONS.ASSIGN) {
      setOpenAssign(true);
    } else if (optionName === SANTANDER_CLABE_ACTIONS.UNASSIGN) {
      setOpenUnassign(true);
    }
  };

  const handleCloseAssignModal = () => {
    setOpenAssign(false);
  };

  const handleCloseUnassignModal = () => {
    setOpenUnassign(false);
  };

  const handleAssignClient = useCallback(
    (clientId) => {
      dispatch(assignClabe(clabe, clientId, handleCloseAssignModal));
    },
    [clabe, dispatch]
  );

  const handleUnassignModal = useCallback(() => {
    dispatch(unassignClabe(clabe, handleCloseUnassignModal));
  }, [clabe, dispatch]);

  return (
    <>
      <TableRow hover>
        <TableCell align="center">{clabe}</TableCell>
        {client ? (
          <TableCell align="center">
            <Link to={`/clients/detail/${client?.id}`}>
              {(client && client.fullname) || t('COMMON.NO_DATA')}
            </Link>
          </TableCell>
        ) : (
          <TableCell align="center">{t('COMMON.NO_DATA')}</TableCell>
        )}
        <TableCell align="right">
          <MoreMenu options={menuOptions} handleClickOption={handleMenuClick} />
        </TableCell>
      </TableRow>

      <AssignClientDialog
        open={openAssign}
        handleClose={handleCloseAssignModal}
        onSubmit={handleAssignClient}
        clabe={clabe}
      />

      <ConfirmationDialog
        open={openUnassign}
        handleClose={handleCloseUnassignModal}
        title={t('ADMINISTRATOR_VIEW.SANTANDER_CLABES.UNASSIGN_CLABE')}
        description={t(
          'ADMINISTRATOR_VIEW.SANTANDER_CLABES.UNASSIGN_CLABE_DESCRIPTION',
          {
            clabe,
            clientName: client?.fullname
          }
        )}
        onSubmit={handleUnassignModal}
        isLoading={isUnassigning}
        width={60}
        descriptionTextAlign="left"
      />
    </>
  );
};

SantanderClabesRow.propTypes = {
  clabe: PropTypes.string.isRequired,
  client: PropTypes.object
};

export default SantanderClabesRow;
